import { Text, Title } from '@components/v2/ui';
import { CellMintStatus } from '@components/v2/ui/Datatable/cells/CellMintStatus';
import {
	ActionIcon,
	Box,
	Button,
	Flex,
	Modal,
	ScrollArea,
} from '@mantine/core';
import { IconTrash, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import classes from './ModalSelectedProductions.module.css';
import { StatusSwitch } from './components/StatusSwitch';

interface ModalSelectedProductionsProps {
	opened: boolean;
	onClose: () => void;
	onConfirm: () => void;
	onDelete: (item: any) => void;
	records?: any[];
}

const ModalSelectedProductions = ({
	opened,
	onClose,
	onConfirm,
	onDelete,
	records = [],
}: ModalSelectedProductionsProps) => {
	const { t } = useTranslation();

	const displayData = records;

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			classNames={{
				content: classes.content,
				body: classes.body,
			}}
			withCloseButton={false}
			size={'1035px'}
			centered
		>
			<Flex
				justify={'space-between'}
				align="center"
				mb={20}
			>
				<Title order={6} className={classes.modalTitle}>
					{`(${displayData.length}) ${t('global.selectedProductions')}`}
				</Title>
				<ActionIcon
					variant="outline"
					radius="xl"
					size={40}
					onClick={onClose}
					className={classes.closeBtn}
				>
					<IconX size={20} />
				</ActionIcon>
			</Flex>

			<ScrollArea.Autosize
				mah={400}
				type="always"
				scrollbarSize={8}
				offsetScrollbars
			>
				<ScrollArea type="auto" scrollbarSize={8} pb="md">
					<Box
						style={{
							minWidth: 'fit-content',
							width: '100%',
						}}
					>
						{/* HEADER */}
						<Flex
							className={classes.tableHeader}
							px="xs"
							mb="xs"
							align="center"
						>
							<Box flex="0 0 100px" pr={10}>
								<Text variant="caption" c={'#9BA6B7'}>
									{t('global.project')}
								</Text>
							</Box>
							<Box flex="0 0 100px" pr={10}>
								<Text variant="caption" c={'#9BA6B7'}>
									{t('global.site')}
								</Text>
							</Box>
							<Box flex="0 0 110px" pr={10}>
								<Text variant="caption" c={'#9BA6B7'}>
									{t('global.startDate')}
								</Text>
							</Box>
							<Box flex="0 0 120px" pr={10}>
								<Text variant="caption" c={'#9BA6B7'}>
									{t('global.quantity')}
								</Text>
							</Box>
							<Box flex="0 0 130px" pr={10}>
								<Text variant="caption" c={'#9BA6B7'}>
									{t('global.tokenEquivalent')}
								</Text>
							</Box>
							<Box flex="0 0 100px" pr={10}>
								<Text variant="caption" c={'#9BA6B7'}>
									{t('global.mintStatus')}
								</Text>
							</Box>
							<Box flex="0 0 120px" pr={10}>
								<Text variant="caption" c={'#9BA6B7'}>
									{t('global.status')}
								</Text>
							</Box>
							<Box style={{ width: 80 }}>
								<Text
									variant="caption"
									c={'#9BA6B7'}
									ta="center"
								>
									{t('global.deselect')}
								</Text>
							</Box>
						</Flex>

						{/* BODY */}
						<Flex direction="column" gap={6}>
							{displayData.map((item, index) => (
								<Flex
									key={item.id || index}
									className={classes.tableRow}
									align="center"
									px="xs"
									py={12}
								>
									<Box flex="0 0 100px" pr={10}>
										<Text
											className={classes.cellTextWrap}
											fw={500}
										>
											{item.project}
										</Text>
									</Box>
									<Box flex="0 0 100px" pr={10}>
										<Text
											className={classes.cellTextWrap}
										>
											{item.site}
										</Text>
									</Box>
									<Box flex="0 0 110px" pr={10}>
										<Text className={classes.cellText}>
											{item.startDate}
										</Text>
									</Box>
									<Box flex="0 0 120px" pr={10}>
										<Text className={classes.cellText}>
											{item.production}
										</Text>
									</Box>
									<Box flex="0 0 130px" pr={10}>
										<Text className={classes.cellText}>
											{item.tokenEquivalent}
										</Text>
									</Box>
									<Box flex="0 0 100px" pr={10}>
										<CellMintStatus row={item} />
									</Box>
									<Box flex="0 0 120px" pr={10}>
										<StatusSwitch
											disabled
											inActiveLabel={t(
												'global.unverified'
											)}
											activeLabel={t('global.verified')}
											active={item.verified}
											readonly={item?.status == 'MINTED'}
										/>
									</Box>
									<Box style={{ width: 80 }}>
										<Flex justify="center">
											<ActionIcon
												variant="subtle"
												color="red"
												radius="md"
												onClick={() => onDelete(item)}
											>
												<IconTrash size={18} />
											</ActionIcon>
										</Flex>
									</Box>
								</Flex>
							))}
						</Flex>
					</Box>
				</ScrollArea>
			</ScrollArea.Autosize>

			<Flex justify="center" mt={25}>
				<Button
					variant="outline"
					radius="xl"
					size="md"
					className={classes.btnDeleteAll}
					onClick={onConfirm}
				>
					{t('global.deselectAll')}
				</Button>
			</Flex>
		</Modal>
	);
};

export default ModalSelectedProductions;
